import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import * as XLSX from 'xlsx';
import {
  Container,
  Table,
  Card,
  Spinner,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./salesreport.css";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../Context/UserContext";
import CurrencySymbol, { resolveCurrencyCode } from "../CurrencySymbol";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FaPrint,
  FaFilePdf,
  FaFileExcel,
  FaEraser,
  FaChevronUp,
  FaChevronDown,
  FaColumns
} from "react-icons/fa";
import { useRef } from "react";

const defaultPrintSettings = {
  headerBlocks: [
    { id: 'h1', text: 'My Restaurant', align: 'center', label: 'Restaurant Name' },
    { id: 'h2', text: '123 Store Street, City', align: 'center', label: 'Street' },
    { id: 'h3', text: 'Phone: +91 123-456-7890', align: 'center', label: 'Phone' },
    { id: 'h4', text: 'GSTIN: 12ABCDE3456F7Z8', align: 'center', label: 'GSTIN' },
  ],
  footerBlocks: [
    { id: 'f1', text: 'Thank You! Visit Again!', align: 'center', label: 'Thank You Message' },
    { id: 'f2', text: 'Powered by MyRestaurant', align: 'center', label: 'Powered By' },
  ]
};

// Reusable Multi-select Checkbox Dropdown Component
const CheckboxDropdown = ({ label, options, selectedValues, onToggle, placeholder, searchPlaceholder, showSearch = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const filteredOptions = options.filter(opt => {
    if (!opt) return false;
    const lbl = opt && typeof opt === 'object' ? (opt.label || opt.name || "") : opt;
    return String(lbl).toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="filter-item checkbox-dropdown-container" ref={dropdownRef}>
      <Form.Label className="fw-bold">{label}</Form.Label>
      <div className={`checkbox-dropdown-header ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(!isOpen)}>
        <div className="selected-labels-container">
          {selectedValues.length > 0 ? (
            <span className="summary-text">{selectedValues.length} Selected</span>
          ) : <span className="placeholder-text">{placeholder}</span>}
        </div>
        <span className="dropdown-arrow">▼</span>
      </div>
      {isOpen && (
        <div className="checkbox-dropdown-menu shadow-lg">
          {showSearch && (
            <div className="p-2 border-bottom">
              <Form.Control
                size="sm"
                type="text"
                placeholder={searchPlaceholder || "Search..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <div className="options-list">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, idx) => {
                const isObj = option && typeof option === 'object';
                const val = isObj ? (option.value !== undefined ? option.value : (option.userId || option.email || option)) : option;
                const lbl = isObj ? (option.label || option.name || String(option)) : String(option);
                const isChecked = selectedValues.includes(val);
                return (
                  <div key={idx} className={`option-item ${isChecked ? 'selected' : ''}`} onClick={() => onToggle(val)}>
                    <Form.Check
                      type="checkbox"
                      checked={isChecked}
                      readOnly
                      label={lbl}
                    />
                  </div>
                );
              })
            ) : <div className="p-2 text-center text-muted">No options found</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const SalesReport = () => {
  const [salesData, setSalesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [filterStartTime, setFilterStartTime] = useState("");
  const [filterEndTime, setFilterEndTime] = useState("");
  const [filterInvoiceNo, setFilterInvoiceNo] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  const [filterItems, setFilterItems] = useState([]); // Array for multi-select
  const [filterCategories, setFilterCategories] = useState([]); // Array for multi-select
  const [filterDeliveryPersons, setFilterDeliveryPersons] = useState([]); // Array for multi-select
  const [filterOrderTypes, setFilterOrderTypes] = useState([]); // Array for multi-select
  const [filterOffers, setFilterOffers] = useState([]); // Array for multi-select
  const [filterPaymentModes, setFilterPaymentModes] = useState([]); // Array for multi-select

  const [itemOptions, setItemOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [deliveryPersonOptions, setDeliveryPersonOptions] = useState([]);
  const [offerOptions, setOfferOptions] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [dynamicFilters, setDynamicFilters] = useState({}); // {designationName: [selectedUserIds]}
  const [branches, setBranches] = useState([]);
  const [filterBranches, setFilterBranches] = useState([]);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [showNoDataModal, setShowNoDataModal] = useState(false);

  const [printSettings, setPrintSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const { baseUrl, configLoading, getHeaders } = useContext(UserContext);
  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [permsLoading, setPermsLoading] = useState(true);
  const [showColumnFilters, setShowColumnFilters] = useState(false);

  const [showColumnModal, setShowColumnModal] = useState(false);
  const [selectedFieldToAdd, setSelectedFieldToAdd] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(0);

  const getInitialSalesReportColumns = () => {
    try {
      const saved = localStorage.getItem('salesReportColumnOrder');
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to load columns', e);
    }
    return [
      { key: "invoice_no", label: "Invoice No", align: "left" },
      { key: "date", label: "Date", align: "center" },
      { key: "time", label: "Time", align: "center" },
      { key: "orderType", label: "Order Type", align: "center" },
      { key: "companyBranch", label: "Company / Branch", align: "left" },
      { key: "total", label: "Total", align: "right" },
      { key: "vat_amount", label: "VAT Amount", align: "right" },
      { key: "grand_total", label: "Grand Total", align: "right" },
      { key: "employee", label: "Employee", align: "left" },
    ];
  };

  const [columnOrder, setColumnOrder] = useState(getInitialSalesReportColumns());

  useEffect(() => {
    localStorage.setItem('salesReportColumnOrder', JSON.stringify(columnOrder));
  }, [columnOrder]);

  const possibleColumns = [
    { key: "invoice_no", label: "Invoice No", align: "left" },
    { key: "date", label: "Date", align: "center" },
    { key: "time", label: "Time", align: "center" },
    { key: "orderType", label: "Order Type", align: "center" },
    { key: "companyBranch", label: "Company / Branch", align: "left" },
    { key: "total", label: "Total", align: "right" },
    { key: "vat_amount", label: "VAT Amount", align: "right" },
    { key: "grand_total", label: "Grand Total", align: "right" },
    { key: "employee", label: "Employee", align: "left" },
    { key: "customer", label: "Customer", align: "left" },
    { key: "phoneNumber", label: "Phone Number", align: "center" },
    { key: "tableNumber", label: "Table Number", align: "center" },
    { key: "redeemed_amount", label: "Points Discount", align: "right" },
  ];

  const addColumn = () => {
    const fieldKey = selectedFieldToAdd;
    if (!fieldKey) return;
    const field = possibleColumns.find(p => p.key === fieldKey);
    if (!field || columnOrder.some(c => c.key === field.key)) return;
    const pos = parseInt(selectedPosition);
    const newOrder = [...columnOrder];
    newOrder.splice(pos, 0, field);
    setColumnOrder(newOrder);
    setSelectedFieldToAdd('');
    setSelectedPosition(0);
  };

  const removeColumn = (index) => {
    const newOrder = [...columnOrder];
    newOrder.splice(index, 1);
    setColumnOrder(newOrder);
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("text/plain", index);
    e.target.classList.add("dragging");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    const target = e.target.closest("th");
    if (target) {
      target.classList.add("drag-over");
    }
  };

  const handleDragLeave = (e) => {
    const target = e.target.closest("th");
    if (target) {
      target.classList.remove("drag-over");
    }
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    const newOrder = [...columnOrder];
    const [draggedColumn] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(targetIndex, 0, draggedColumn);
    setColumnOrder(newOrder);
    document.querySelectorAll(".premium-table th").forEach((th) => {
      th.classList.remove("drag-over");
      th.classList.remove("dragging");
    });
  };

  const handleDragEnd = (e) => {
    document.querySelectorAll(".premium-table th").forEach((th) => {
      th.classList.remove("drag-over");
      th.classList.remove("dragging");
    });
  };

  const [settings, setSettings] = useState({
    currency: 'AED',
    currencyPrecision: 2,
    language: 'en-US',
    dateFormat: 'dd-mm-yyyy',
    timeFormat: 'HH:mm:ss',
    timeZone: 'Asia/Dubai',
    useCurrencySymbol: false,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const calculateItemPrices = (item) => {
    let baseAmount = parseFloat(item.price || item.basePrice || 0);
    let totalAmount = baseAmount * (parseInt(item.quantity) || 1);

    // Add ice and spicy prices
    const icePrice = item.icePreference === "with_ice" ? parseFloat(item.icePrice || item.ice_price || 0) : 0;
    const spicyPrice = item.isSpicy ? parseFloat(item.spicyPrice || item.spicy_price || 0) : 0;
    totalAmount += (icePrice + spicyPrice) * (parseInt(item.quantity) || 1);

    if (item.customVariantsDetails) {
      Object.values(item.customVariantsDetails).forEach(variant => {
        const qty = item.customVariantsQuantities?.[variant.name] || 1;
        totalAmount += parseFloat(variant.price || 0) * qty * (parseInt(item.quantity) || 1);
      });
    }

    if (item.itemToppings) {
      Object.values(item.itemToppings).forEach(top => {
        totalAmount += parseFloat(top.price || 0) * (top.qty || 1) * (parseInt(item.quantity) || 1);
      });
    }

    if (item.addons) {
      item.addons.forEach(a => {
        totalAmount += parseFloat(a.addon_price || 0) * (parseInt(a.addon_quantity) || 0);
      });
    }
    if (item.selectedCombos) {
      item.selectedCombos.forEach(c => {
        totalAmount += parseFloat(c.combo_price || 0) * (parseInt(c.combo_quantity) || 0);
      });
    }
    return { baseAmount, totalAmount };
  };

  const calculateSubtotal = (sale) => {
    if (sale.total && parseFloat(sale.total) > 0) return parseFloat(sale.total);
    const gt = parseFloat(sale.grand_total || sale.grandTotal || 0);
    const vat = parseFloat(sale.vat_amount || 0);
    if (gt > 0) return gt - vat;
    return (sale.items || []).reduce((sum, item) => sum + calculateItemPrices(item).totalAmount, 0);
  };

  const calculateVAT = (sale) => parseFloat(sale.vat_amount) || 0;

  const calculateGrandTotal = (sale) => {
    if (sale.grand_total && parseFloat(sale.grand_total) > 0) return parseFloat(sale.grand_total);
    if (sale.grandTotal && parseFloat(sale.grandTotal) > 0) return parseFloat(sale.grandTotal);
    return calculateSubtotal(sale) + calculateVAT(sale);
  };

  const getFormattedDate = (dateInput, dateFormat = settings.dateFormat, timeZone = settings.timeZone) => {
    if (!dateInput) return '';
    let date;
    if (dateInput instanceof Date) { date = dateInput; }
    else if (typeof dateInput === 'string') {
      if (dateInput.includes('T')) {
        date = new Date(dateInput);
      } else {
        const parts = dateInput.split(/[-/]/);
        let year, month, day;
        if (parts[0].length === 4) {
          year = parts[0]; month = parts[1]; day = parts[2];
        } else {
          year = parts[2]; month = parts[1]; day = parts[0];
        }
        // Direct string formatting to avoid timezone offset shifts
        switch (dateFormat) {
          case 'dd-mm-yyyy': return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
          case 'mm-dd-yyyy': return `${month.padStart(2, '0')}-${day.padStart(2, '0')}-${year}`;
          case 'yyyy-mm-dd': return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          default: return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
        }
      }
    }
    else { return dateInput || ''; }
    if (isNaN(date.getTime())) return dateInput || '';

    const tzOptions = { timeZone: timeZone || 'UTC' };
    const numericFormatter = new Intl.DateTimeFormat('en', { ...tzOptions, year: 'numeric', month: '2-digit', day: 'numeric' });
    const parts = numericFormatter.formatToParts(date);
    const yearStr = parts.find((p) => p.type === 'year')?.value || '';
    const monthStr = parts.find((p) => p.type === 'month')?.value || '';
    const dayStr = parts.find((p) => p.type === 'day')?.value || '';

    switch (dateFormat) {
      case 'dd-mm-yyyy': return `${dayStr.padStart(2, '0')}-${monthStr}-${yearStr}`;
      case 'mm-dd-yyyy': return `${monthStr}-${dayStr.padStart(2, '0')}-${yearStr}`;
      case 'yyyy-mm-dd': return `${yearStr}-${monthStr}-${dayStr.padStart(2, '0')}`;
      default: return `${dayStr.padStart(2, '0')}-${monthStr}-${yearStr}`;
    }
  };

  const getFormattedTime = (timeInput, timeFormat = settings.timeFormat, timeZone = settings.timeZone) => {
    if (!timeInput) return '';
    let date;
    if (timeInput instanceof Date) { date = timeInput; }
    else if (typeof timeInput === 'string') {
      if (timeInput.includes('T')) {
        date = new Date(timeInput);
      } else {
        const [timePart, period] = timeInput.trim().split(' ');
        let [hours, minutes, seconds = '00'] = timePart.split(':').map(x => parseInt(x, 10) || 0);
        if (period && (period.toUpperCase() === 'PM' && hours !== 12)) hours += 12;
        else if (period && (period.toUpperCase() === 'AM' && hours === 12)) hours = 0;
        date = new Date(2023, 0, 1, hours, minutes, seconds);
      }
    } else { return timeInput || ''; }
    if (isNaN(date.getTime())) return timeInput || '';

    const tzOptions = { timeZone: timeZone || 'UTC' };
    const is12Hour = timeFormat.includes(' a') || timeFormat.startsWith('hh');
    const options = {
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: is12Hour,
      ...((timeInput instanceof Date || (typeof timeInput === 'string' && timeInput.includes('T'))) && tzOptions)
    };
    return date.toLocaleTimeString('en-US', options);
  };

  const getEmployeeName = (userId) => {
    if (!userId || userId === "N/A") return "N/A";
    const employee = (allEmployees || []).find(emp =>
      String(emp.userId) === String(userId) ||
      String(emp.email) === String(userId) ||
      String(emp.username) === String(userId)
    );
    return employee ? (employee.name || employee.firstName || userId) : userId;
  };

  // Returns JSX — shows employee + delivery person (for Online Delivery)
  const getEmployeeDisplay = (sale) => {
    const isOnlineDelivery = (sale.orderType || '').toLowerCase().includes('online') ||
      (sale.orderType || '').toLowerCase().includes('delivery');
    const empName = getEmployeeName(sale.userId);
    const deliveryName = sale.deliveryPersonName || '';

    if (isOnlineDelivery && deliveryName) {
      return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {empName !== 'N/A' && (
            <span style={{ fontSize: '13px', color: '#1B1B29', fontWeight: '500' }}>{empName}</span>
          )}
          <span style={{ fontSize: '11px', color: '#604BE8', fontWeight: '600' }}>
            🚚 {deliveryName}
          </span>
        </span>
      );
    }
    return empName;
  };

  // Returns plain text — for Excel export
  const getEmployeeDisplayText = (sale) => {
    const isOnlineDelivery = (sale.orderType || '').toLowerCase().includes('online') ||
      (sale.orderType || '').toLowerCase().includes('delivery');
    const empName = getEmployeeName(sale.userId);
    const deliveryName = sale.deliveryPersonName || '';
    if (isOnlineDelivery && deliveryName) {
      if (empName !== 'N/A') return `${empName} + ${deliveryName}`;
      return deliveryName;
    }
    return empName;
  };

  const cleanData = (data, currentSettings = settings) => {
    if (!Array.isArray(data)) return [];
    return data
      .filter((sale) => sale.items && sale.items.length > 0 && sale.invoice_no)
      .map((sale) => ({
        ...sale,
        rawDate: sale.date,
        date: getFormattedDate(sale.date || sale.created_at, currentSettings.dateFormat, currentSettings.timeZone),
        time: getFormattedTime(sale.time || sale.created_at, currentSettings.timeFormat, currentSettings.timeZone),
        invoice_currency: sale.invoice_currency || sale.currency || currentSettings.currency || 'AED',
        invoice_currency_precision: currentSettings.currencyPrecision || 2,
      }));
  };

  useEffect(() => {
    if (configLoading) return;

    const initializeData = async () => {
      setLoading(true);
      setPermsLoading(true);
      setError(null);
      try {
        let hasReadPermission = false;
        let userObj = null;
        let userRole = null;
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            userObj = JSON.parse(userStr);
            userRole = userObj.role;
            if (userRole) {
              const API_URL = baseUrl || "";
              const lowRole = userRole?.toLowerCase().replace(/[\s_]+/g, '');
              const isGroup = lowRole === 'superadmin' || lowRole === 'groupadmin' || lowRole === 'tenantadmin';
              const isComp = (lowRole === 'companyadmin' || lowRole === 'admin') && !userObj.branch && !userObj.branch_name;

              setIsGroupAdmin(isGroup);
              setIsCompanyAdmin(isComp);
              const response = await axios.get(`${API_URL}/api/role-permissions?role=${userRole}`);
              const perms = response.data.permissions || [];
              const pagePerm = perms.find(p => p.pageId === 'sales_report');
              if (pagePerm) {
                setCanRead(pagePerm.canRead === true);
                setCanWrite(pagePerm.canWrite === true);
                hasReadPermission = pagePerm.canRead === true;
              }
            }
          } catch (e) {
            console.error("Error checking permissions", e);
          }
        }

        if (hasReadPermission) {
          const API_URL = baseUrl || '';
          const endpoints = [
            {
              key: 'sales',
              url: `${API_URL}/api/sales`,
              headers: getHeaders()
            },
            { key: 'settings', url: `${API_URL}/api/settings` },
            { key: 'print', url: `${API_URL}/api/print_settings/active` },
            { key: 'logo', url: `${API_URL}/api/logo` },
            { key: 'items', url: `${API_URL}/api/items` },
            { key: 'users', url: `${API_URL}/api/users` },
            { key: 'designations', url: `${API_URL}/api/employee-designations` },
            { key: 'employees', url: `${API_URL}/api/add-employee` },
            {
              key: 'branches',
              url: `${API_URL}/api/branches`,
              headers: userObj?.company ? { 'X-Company-Name': userObj.company } : {}
            }
          ];

          const results = await Promise.allSettled(
            endpoints.map(ep => axios.get(ep.url, { headers: ep.headers || {} }))
          );

          const data = {};
          results.forEach((result, index) => {
            const key = endpoints[index].key;
            if (result.status === 'fulfilled') {
              data[key] = result.value.data;
            } else {
              console.warn(`Failed to fetch ${key}:`, result.reason?.message);
            }
          });

          let fetchedSettings = { ...settings, ...(data.settings || {}) };
          setSettings(fetchedSettings);

          if (data.sales) {
            const cleaned = cleanData(data.sales, fetchedSettings);
            setSalesData(cleaned);
            if (cleaned.length === 0) {
              setShowNoDataModal(true);
            }
            setOfferOptions([...new Set(cleaned.flatMap(s => s.items.map(i => i.offer_description).filter(Boolean)))].sort());
            setDeliveryPersonOptions([...new Set(cleaned.map(s => s.deliveryPersonName).filter(Boolean))].sort());
          }

          if (data.print) setPrintSettings(data.print);
          else setPrintSettings(defaultPrintSettings);

          if (data.logo && data.logo.logo) setLogoUrl(API_URL + data.logo.logo);

          if (data.items && Array.isArray(data.items)) {
            setItemOptions(data.items.map(i => ({ name: i.item_name, category: i.item_group || "N/A" })));
            setCategoryOptions([...new Set(data.items.map(i => i.item_group))].filter(Boolean));
          }

          if (data.designations && Array.isArray(data.designations)) {
            const filterDesignations = data.designations.filter(d => d.show_in_sales_filter);
            setDesignations(filterDesignations);

            setDynamicFilters(prev => {
              const newState = { ...prev };
              filterDesignations.forEach(d => {
                if (!newState[d.name]) newState[d.name] = [];
              });
              return newState;
            });
          }

          if (data.employees && Array.isArray(data.employees)) {
            setAllEmployees(data.employees);
          }

          if (data.branches && Array.isArray(data.branches)) {
            setBranches(data.branches.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name)));
          }

          const uRole = userRole?.toLowerCase().replace(/[\s_]+/g, '');
          const isGrp = uRole === 'superadmin' || uRole === 'groupadmin' || uRole === 'tenantadmin';

          if (isGrp) {
            try {
              const compRes = await axios.get(`${API_URL}/api/user-companies`, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                }
              });
              const comps = compRes.data.companies || (Array.isArray(compRes.data) ? compRes.data : []);
              const allComps = [...new Set(comps.map(c => c.company_name || c.company).filter(Boolean))].sort();
              setAvailableCompanies(allComps);
            } catch (err) {
              console.error("Failed to fetch companies:", err);
            }
          }
        }
      } catch (err) {
        setError("Error fetching data: " + err.message);
      } finally {
        setLoading(false);
        setPermsLoading(false);
      }
    };

    initializeData();
  }, [baseUrl, configLoading]);

  const getCurrencyFormatter = (invoiceCurrency = null, invoicePrecision = null, asHtml = false, asReact = false) => {
    // Resolve currency: per-invoice first, then active company/branch context from currency_map
    const activeComp = selectedCompanies.length === 1 ? selectedCompanies[0] : (selectedCompanies.length > 1 ? 'All' : localStorage.getItem('active_company') || '');
    const activeBranch = filterBranches.length === 1 ? filterBranches[0] : localStorage.getItem('active_branch') || '';
    const currency = invoiceCurrency || resolveCurrencyCode(null, activeComp, activeBranch);
    const precision = invoicePrecision !== null ? parseInt(invoicePrecision) : parseInt(settings.currencyPrecision) || 2;
    return {
      format: (value) => {
        const numValue = parseFloat(value) || 0;
        const fixedValue = numValue.toFixed(precision);
        if (asReact) {
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <span style={{ marginRight: '2px', transform: 'translateY(1px)' }}>
                <CurrencySymbol currencyCode={currency} size={14} />
              </span>
              {fixedValue}
            </span>
          );
        } else if (asHtml) {
          if (currency?.toUpperCase() === 'AED') {
            return `<img src="/assets/Dirham Currency Symbol - Black.svg" style="height: 12px; vertical-align: middle; margin-right: 2px; filter: brightness(0.1);" alt="AED"/> ${fixedValue}`;
          } else {
            const symbols = {
              INR: "₹", USD: "$", EUR: "€", GBP: "£", JPY: "¥", CNY: "¥",
              SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
              QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
              CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
            };
            return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
          }
        } else {
          const symbols = {
            INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", JPY: "¥", CNY: "¥",
            SGD: "S$", MYR: "RM", THB: "฿", IDR: "Rp", KRW: "₩", PHP: "₱", SAR: "﷼",
            QAR: "﷼", KWD: "د.ك", OMR: "﷼", BHD: ".د.ب", CAD: "CA$", AUD: "A$", NZD: "NZ$",
            CHF: "CHF", ZAR: "R", BRL: "R$", PKR: "₨", LKR: "Rs", NGN: "₦"
          };
          return `${symbols[currency?.toUpperCase()] || currency} ${fixedValue}`;
        }
      }
    };
  };

  const formatCurrency = (value, sale = null) => {
    const currency = sale?.invoice_currency || resolveCurrencyCode(null,
      selectedCompanies.length === 1 ? selectedCompanies[0] : (selectedCompanies.length > 1 ? 'All' : localStorage.getItem('active_company') || ''),
      filterBranches.length === 1 ? filterBranches[0] : localStorage.getItem('active_branch') || ''
    );
    const precision = sale?.invoice_currency_precision || settings.currencyPrecision;
    return getCurrencyFormatter(currency, precision, false, true).format(value);
  };

  // RE-FETCH Sales data when company/branch filters change (interactive filtering)
  useEffect(() => {
    if (configLoading || permsLoading || !canRead) return;

    const fetchFilteredSales = async () => {
      try {
        const API_URL = baseUrl || "";
        // Pass selected company/branch directly into getHeaders so they take priority
        const selectedComp = selectedCompanies.length > 0 ? selectedCompanies.join(',') : null;
        const selectedBranch = filterBranches.length > 0 ? filterBranches.join(',') : null;
        const headers = getHeaders(selectedBranch, selectedComp);

        const response = await axios.get(`${API_URL}/api/sales`, { headers });
        const cleaned = cleanData(response.data);
        setSalesData(cleaned);
      } catch (err) {
        console.error("Error re-fetching sales report:", err);
      }
    };

    fetchFilteredSales();
  }, [selectedCompanies, filterBranches, baseUrl, canRead, permsLoading]);

  // RE-FETCH Branches when selected companies change
  useEffect(() => {
    if (!isGroupAdmin || selectedCompanies.length === 0) return;

    const fetchBranchesForCompanies = async () => {
      try {
        const API_URL = baseUrl || "";
        const headers = { 'X-Company-Name': selectedCompanies.join(',') };
        const response = await axios.get(`${API_URL}/api/branches`, { headers });
        setBranches(response.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name)));
      } catch (err) {
        console.error("Error fetching branches for companies in report:", err);
      }
    };

    fetchBranchesForCompanies();
  }, [selectedCompanies, baseUrl, isGroupAdmin]);

  const calculateAggregates = (sales) => {
    const currencyTotals = new Map();
    const dashboardMetrics = { totalPosRevenue: 0, cashPayments: 0, cardPayments: 0, upiPayments: 0, aggregatorPayments: 0, totalDiscounts: 0, netTotal: 0, totalRedeemedPoints: 0 };
    sales.forEach((sale) => {
      const sub = calculateSubtotal(sale);
      const vat = calculateVAT(sale);
      const grand = calculateGrandTotal(sale);
      const curr = sale.invoice_currency || settings.currency || 'AED';
      const prec = sale.invoice_currency_precision || settings.currencyPrecision || 2;
      if (!currencyTotals.has(curr)) currencyTotals.set(curr, { subtotal: 0, vat: 0, grand: 0, redeemedAmount: 0, precision: prec });
      const t = currencyTotals.get(curr);
      t.subtotal += sub; t.vat += vat; t.grand += grand;
      t.redeemedAmount += parseFloat(sale.redeemed_amount || 0);
      
      // Assume all metrics are summed in base/default currency equivalent for the dashboard view
      dashboardMetrics.totalPosRevenue += grand;
      dashboardMetrics.netTotal += sub;
      dashboardMetrics.totalRedeemedPoints += parseFloat(sale.redeemed_amount || 0);
      
      const pm = (sale.payments?.[0]?.mode_of_payment || sale.paymentMode || sale.payment_mode || "CASH").toUpperCase();
      if (pm.includes("CASH")) dashboardMetrics.cashPayments += grand;
      else if (pm.includes("CARD")) dashboardMetrics.cardPayments += grand;
      else if (pm.includes("UPI")) dashboardMetrics.upiPayments += grand;
      else if (pm.includes("ZOMATO") || pm.includes("SWIGGY") || pm.includes("NOON")) dashboardMetrics.aggregatorPayments += grand;
      
      // Calculate discount
      if (sale.discountType === "Fixed Amount") {
        dashboardMetrics.totalDiscounts += parseFloat(sale.discountValue) || 0;
      } else if (sale.discountType === "Percentage") {
        const d = (parseFloat(sale.discountValue) || 0) / 100 * parseFloat(sale.totalAmount);
        dashboardMetrics.totalDiscounts += isNaN(d) ? 0 : d;
      }
    });
    return { currencyTotals, dashboardMetrics };
  };

  const filteredSales = salesData.filter((sale) => {
    const parseSDate = (s) => { const p = s.split('-'); return new Date(p[2], p[1] - 1, p[0]); };
    const sDate = parseSDate(sale.date);
    const from = fromDate ? new Date(fromDate) : null;
    if (from) from.setHours(0, 0, 0, 0);
    const to = toDate ? new Date(toDate) : null;
    if (to) to.setHours(23, 59, 59, 999);

    const dateMatch = (!from && !to) || (from && !to && sDate >= from) || (!from && to && sDate <= to) || (from && to && sDate >= from && sDate <= to);

    let matchesTime = true;
    if (filterStartTime || filterEndTime) {
      const parseTime = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m; };
      const saleT = parseTime(sale.time);
      if (filterStartTime && saleT < parseTime(filterStartTime)) matchesTime = false;
      if (filterEndTime && saleT > parseTime(filterEndTime)) matchesTime = false;
    }

    const matchesInvoice = !filterInvoiceNo || sale.invoice_no.toLowerCase().includes(filterInvoiceNo.toLowerCase());
    const matchesCustomer = !filterCustomer || (sale.customer || "").toLowerCase().includes(filterCustomer.toLowerCase());
    const matchesPhone = !filterPhone || (sale.phone || sale.customer_phone || "").includes(filterPhone);

    // Multi-select Matches
    const matchesItems = filterItems.length === 0 || (sale.items || []).some(i => filterItems.includes(i.item_name));
    const matchesCategories = filterCategories.length === 0 || (sale.items || []).some(i => filterCategories.includes(i.item_group || i.category));
    const matchesDelivery = filterDeliveryPersons.length === 0 || filterDeliveryPersons.includes(sale.deliveryPersonName);
    const matchesOffers = filterOffers.length === 0 || (sale.items || []).some(i => filterOffers.includes(i.offer_description || i.offer_name));

    // Order Type Matching (Multi-select)
    let matchesOrderTypes = filterOrderTypes.length === 0;
    if (!matchesOrderTypes) {
      const type = (sale.orderType || "").toUpperCase();
      matchesOrderTypes = filterOrderTypes.some(ft => {
        if (ft === "DIN") return type.includes("DIN") || type.includes("DINE");
        if (ft === "TAK") return type.includes("TAK") || type.includes("TAKE");
        if (ft === "OND") return type.includes("OND") || type.includes("ONLINE") || type.includes("DELIVERY");
        return type.includes(ft.toUpperCase());
      });
    }

    // Payment Mode Matching (Multi-select)
    let matchesPaymentModes = filterPaymentModes.length === 0;
    if (!matchesPaymentModes) {
      const pm = (sale.payments?.[0]?.mode_of_payment || sale.paymentMode || sale.payment_mode || "CASH").toUpperCase();
      matchesPaymentModes = filterPaymentModes.some(fpm => pm.includes(fpm.toUpperCase()));
    }

    // Dynamic Designation Filters
    let matchesDynamic = true;
    Object.entries(dynamicFilters).forEach(([designationName, selectedUserIds]) => {
      if (selectedUserIds.length > 0) {
        // Find if this sale matches any of the selected users for this designation
        const matchesThisDesignation = selectedUserIds.includes(sale.userId) ||
          selectedUserIds.includes(sale.bearerId) ||
          allEmployees.some(emp => selectedUserIds.includes(emp.userId) && (emp.name === sale.bearerName || emp.name === sale.deliveryPersonName));

        if (!matchesThisDesignation) matchesDynamic = false;
      }
    });

    const saleBranch = sale.branch_name || sale.branch;
    // Helper: detect if this sale is a company-level (no specific branch) record
    const isCompanyLevelSale = !saleBranch ||
      ['', 'all', 'all branches', 'global', 'unassigned', 'nan'].includes(String(saleBranch).trim().toLowerCase());

    let matchesBranch;
    if (filterBranches.length > 0) {
      // Branch explicitly selected → match only that branch
      matchesBranch = filterBranches.includes(saleBranch);
    } else if (selectedCompanies.length > 0) {
      // Company selected but NO branch → show only company-level (All Branches) sales
      matchesBranch = isCompanyLevelSale;
    } else {
      // No company/branch filter → show everything
      matchesBranch = true;
    }

    // Company match (client-side safety net for when backend returns mixed company data)
    const matchesCompany = selectedCompanies.length === 0 ||
      selectedCompanies.some(sc => {
        const sc_l = sc.trim().toLowerCase();
        const cn = (sale.company_name || sale.company || '').trim().toLowerCase();
        return cn === sc_l;
      });

    return dateMatch && matchesTime && matchesInvoice && matchesCustomer && matchesPhone &&
      matchesOrderTypes && matchesDelivery && matchesOffers &&
      matchesCategories && matchesItems && matchesPaymentModes && matchesDynamic && matchesBranch && matchesCompany;
  });


  const handleClearFilters = () => {
    setFromDate(new Date()); setToDate(new Date());
    setFilterStartTime(""); setFilterEndTime("");
    setFilterInvoiceNo(""); setFilterCustomer("");
    setFilterPhone("");
    setFilterItems([]);
    setFilterCategories([]);
    setFilterOffers([]);
    setFilterOrderTypes([]);
    setFilterDeliveryPersons([]);
    setFilterPaymentModes([]);
    setFilterBranches([]);
    setSelectedCompanies([]);

    setDynamicFilters(prev => {
      const newState = { ...prev };
      Object.keys(newState).forEach(k => newState[k] = []);
      return newState;
    });
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    const dateStr = new Date().toISOString().split('T')[0];
    document.title = `salesreport_${dateStr}`;
    window.print();
    document.title = originalTitle;
  };

  const handleExportExcel = () => {
    const dataToExport = filteredSales.map(sale => ({
      "Invoice No": sale.invoice_no,
      "Date": sale.date,
      "Time": sale.time,
      "Order Type": sale.orderType || "N/A",
      "Total": calculateSubtotal(sale),
      "VAT Amount": calculateVAT(sale),
      "Grand Total": calculateGrandTotal(sale),
      "Employee": getEmployeeDisplayText(sale)
    }));
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Report");
    XLSX.writeFile(wb, `Sales_Report_${getFormattedDate(new Date())}.xlsx`);
  };

  if (permsLoading && loading) return <div className="text-center mt-5"><Spinner animation="border" /><p>Loading Sales Report...</p></div>;
  if (!canRead && !loading) return <div className="text-center mt-5"><div className="alert alert-danger">No Permission to view this report</div><Button variant="primary" onClick={() => navigate('/home')}>Go Home</Button></div>;

  const aggregates = calculateAggregates(filteredSales);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#F3F6FB', minHeight: '100vh', padding: '0' }}>
      <div id="datepicker-portal" />
      <style>
        {`
        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 16px;
          margin-bottom: 24px;
          width: 100%;
        }
        .dashboard-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.03);
          display: flex;
          flex-direction: column;
          gap: 8px;
          border: 1px solid #f0f0f0;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .dashboard-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.06);
        }
        .dashboard-card-title {
          font-size: 11px;
          font-weight: 700;
          color: #8c98a4;
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }
        .dashboard-card-value {
          font-size: 24px;
          font-weight: 800;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .dashboard-card-subtitle {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .premium-table {
          width: 100% !important;
          border-collapse: separate !important;
          border-spacing: 0 !important;
          background: #ffffff !important;
        }
        .premium-table th {
          background: #00d284 !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          text-transform: capitalize !important;
          letter-spacing: 0.5px !important;
          padding: 18px 24px !important;
          border-bottom: none !important;
          text-align: left;
        }
        .premium-table th:first-child {
          border-top-left-radius: 8px !important;
        }
        .premium-table th:last-child {
          border-top-right-radius: 8px !important;
        }
        .premium-table td {
          padding: 18px 24px !important;
          color: #4A4A5A !important;
          font-size: 14px !important;
          font-weight: 500 !important;
          border-bottom: 1px solid #F1F3F8 !important;
          vertical-align: middle !important;
        }
        .premium-table tr:last-child td {
          border-bottom: none !important;
        }
        .premium-table tbody tr {
          transition: background-color 0.2s ease !important;
        }
        .premium-table tbody tr:hover {
          background-color: #F8F9FD !important;
        }
        
        .premium-filter-select {
          height: 44px !important;
          padding: 0 16px !important;
          border-radius: 8px !important;
          border: 1px solid #E2E6EF !important;
          font-size: 14px !important;
          background-color: #ffffff !important;
          transition: all 0.2s !important;
          outline: none !important;
          font-weight: 500 !important;
          color: #1B1B29 !important;
          box-shadow: none !important;
          width: 100% !important;
        }
        .premium-filter-select:focus {
          border-color: #604BE8 !important;
          box-shadow: 0 0 0 4px rgba(96, 75, 232, 0.15) !important;
        }
        .checkbox-dropdown-container .form-label { display: none; }
        .checkbox-dropdown-header {
          height: 44px;
          border-radius: 8px;
          border: 1px solid #E2E6EF;
          background: #ffffff;
          padding: 0 16px;
          display: flex;
          align-items: center;
          font-weight: 500;
          font-size: 14px;
          color: #1B1B29;
        }
        
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .premium-card {
          animation: fadeInScale 0.4s cubic-bezier(0, 0, 0.2, 1);
          background: #ffffff;
          border-radius: 10px;
          box-shadow: 0 5px 25px rgba(0, 0, 0, 0.03);
          border: 1px solid #E2E6EF;
        }
        .action-btn-primary {
          height: 44px;
          padding: 0 24px;
          background-color: #604BE8;
          color: #ffffff;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          box-shadow: 0 4px 15px rgba(96, 75, 232, 0.25);
          transition: all 0.2s;
        }
        .action-btn-primary:hover {
          background-color: #4B76E6;
          transform: translateY(-1px);
        }
        .action-btn-secondary {
          height: 44px;
          padding: 0 16px;
          border-radius: 8px;
          background-color: #ffffff;
          border: 1px solid #E2E6EF;
          color: #4A4A5A;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s;
        }
        .action-btn-secondary:hover {
          background-color: #eff2f9;
          border-color: #604BE8;
          color: #604BE8;
        }

        @media print {
          @page {
            size: landscape;
            margin: 0;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            box-sizing: border-box !important;
          }
          body, html, #root {
            background-color: #ffffff !important;
            color: #000000 !important;
            display: block !important;
            height: auto !important;
            padding: 10mm !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          nav, header, .navbar, .navbar-placeholder, .back-nav-btn, .navbar-toggler, .hamburger, button {
            display: none !important;
          }
          .premium-card, .summary-print-container {
            box-shadow: none !important;
            border: none !important;
            max-height: none !important;
            overflow: visible !important;
            padding: 0 !important;
            margin: 0 !important;
            display: block !important;
          }
          .print-wrapper, .print-header {
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          div[style*="overflow"] {
            overflow: visible !important;
            max-height: none !important;
          }
          .premium-table {
            width: 100% !important;
            min-width: 100% !important;
            border-collapse: collapse !important;
            border: 1px solid #e2e8f0 !important;
            table-layout: fixed !important;
            word-wrap: break-word !important;
          }
          .premium-table thead {
            display: table-header-group !important;
          }
          .premium-table tfoot {
            display: table-row-group !important;
          }
          .premium-table th {
            background-color: #00d284 !important;
            color: #ffffff !important;
            border: 1px solid #00d284 !important;
            padding: 6px !important;
            font-size: 11px !important;
            font-weight: 600 !important;
          }
          .premium-table td {
            color: #1e293b !important;
            border: 1px solid #e2e8f0 !important;
            padding: 6px !important;
            font-size: 10px !important;
            word-break: break-word !important;
          }
          .premium-table tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-after: auto !important;
          }
          .summary-print-container div, .summary-print-container span {
            color: #000000 !important;
          }
          .summary-print-container {
            border-top: 2px solid #000000 !important;
            margin-top: 20px !important;
            padding-top: 20px !important;
          }
          .no-print {
            display: none !important;
          }
          .dashboard-grid {
            display: none !important;
          }
        }
        `}
      </style>

      {/* Top Bar Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '36px 48px 16px 48px' }} className="no-print">
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
          </div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <h1 style={{ margin: 0, fontSize: '32px', fontWeight: '800', color: '#1B1B29', letterSpacing: '-0.5px' }}>Sales Report</h1>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '16px', flexWrap: 'wrap' }}>
            {(fromDate || toDate || filterInvoiceNo || filterCustomer || filterPhone || filterStartTime || filterEndTime ||
              filterItems.length > 0 || filterCategories.length > 0 ||
              filterDeliveryPersons.length > 0 || filterOrderTypes.length > 0 || filterOffers.length > 0 ||
              filterPaymentModes.length > 0 || filterBranches.length > 0 || selectedCompanies.length > 0 || Object.values(dynamicFilters).some(v => v.length > 0)) &&
              <button className="action-btn-secondary" onClick={handleClearFilters}><FaEraser /> Clear</button>}
            <button className="action-btn-secondary" onClick={handleExportExcel}><FaFileExcel /> Excel</button>
            <button className="action-btn-secondary" onClick={handlePrint}><FaFilePdf /> PDF</button>
            <button className="action-btn-secondary" onClick={handlePrint}><FaPrint /> Print Report</button>
            <button className="action-btn-secondary" onClick={() => setShowColumnModal(true)}>Manage Columns</button>
            <button className="action-btn-primary" onClick={() => navigate("/sales-kanban", { state: location.state })}>Kanban View</button>
          </div>
      </div>

      {/* Filter Section */}
      <div style={{ padding: "0 48px", marginBottom: "24px" }} className="no-print">
        <div 
          onClick={() => setShowColumnFilters(!showColumnFilters)} 
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', marginBottom: '16px', background: '#fff', padding: '12px 24px', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)', border: '1px solid #E2E6EF' }}
        >
          <span style={{ fontWeight: '700', fontSize: '15px', color: '#604BE8' }}>Active Column Filters</span>
          {showColumnFilters ? <FaChevronUp style={{ color: '#604BE8', fontSize: '14px' }} /> : <FaChevronDown style={{ color: '#604BE8', fontSize: '14px' }} />}
        </div>
        
        {showColumnFilters && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', padding: '20px 24px', background: '#fff', borderRadius: '10px', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)', border: '1px solid #E2E6EF' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>From Date</label>
              <DatePicker selected={fromDate} onChange={setFromDate} dateFormat="dd-MM-yyyy" className="premium-filter-select" placeholderText="From Date" isClearable todayButton="Today" portalId="datepicker-portal" popperProps={{ strategy: "fixed" }} popperPlacement="bottom-start" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>To Date</label>
              <DatePicker selected={toDate} onChange={setToDate} dateFormat="dd-MM-yyyy" className="premium-filter-select" minDate={fromDate} placeholderText="To Date" isClearable todayButton="Today" portalId="datepicker-portal" popperProps={{ strategy: "fixed" }} popperPlacement="bottom-start" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Start Time</label>
              <input type="time" className="premium-filter-select" value={filterStartTime} onChange={e => setFilterStartTime(e.target.value)} onClick={(e) => e.target.showPicker && e.target.showPicker()} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>End Time</label>
              <input type="time" className="premium-filter-select" value={filterEndTime} onChange={e => setFilterEndTime(e.target.value)} onClick={(e) => e.target.showPicker && e.target.showPicker()} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Invoice No</label>
              <input type="text" className="premium-filter-select" value={filterInvoiceNo} onChange={e => setFilterInvoiceNo(e.target.value)} placeholder="Invoice No..." />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Customer Name</label>
              <input type="text" className="premium-filter-select" value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)} placeholder="Customer Name..." />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Phone Number</label>
              <input type="text" className="premium-filter-select" value={filterPhone} onChange={e => setFilterPhone(e.target.value)} placeholder="Phone Number..." />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Items</label>
              <CheckboxDropdown label="" options={itemOptions.map(i => ({ label: `${i.name} (${i.category})`, value: i.name }))} selectedValues={filterItems} onToggle={(val) => setFilterItems(prev => prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val])} placeholder="All Items" showSearch={true} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Categories</label>
              <CheckboxDropdown label="" options={categoryOptions} selectedValues={filterCategories} onToggle={(val) => setFilterCategories(prev => prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val])} placeholder="All Categories" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Offers</label>
              <CheckboxDropdown label="" options={offerOptions} selectedValues={filterOffers} onToggle={(val) => setFilterOffers(prev => prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val])} placeholder="All Offers" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Delivery Persons</label>
              <CheckboxDropdown label="" options={deliveryPersonOptions} selectedValues={filterDeliveryPersons} onToggle={(val) => setFilterDeliveryPersons(prev => prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val])} placeholder="All Delivery Persons" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Order Types</label>
              <CheckboxDropdown label="" options={[ { label: "Dine In", value: "DIN" }, { label: "Takeaway", value: "TAK" }, { label: "Online Delivery", value: "OND" } ]} selectedValues={filterOrderTypes} onToggle={(val) => setFilterOrderTypes(prev => prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val])} placeholder="All Types" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Payment Modes</label>
              <CheckboxDropdown label="" options={["CASH", "CARD", "UPI", "ZOMATO", "SWIGGY", "NOON"]} selectedValues={filterPaymentModes} onToggle={(val) => setFilterPaymentModes(prev => prev.includes(val) ? prev.filter(i => i !== val) : [...prev, val])} placeholder="All Modes" />
            </div>
            {designations.map((designation) => {
              const designationEmployees = allEmployees.filter(emp => (emp.employeeDesignation || emp.designation) === designation.name).map(emp => ({ label: emp.name || emp.firstName || emp.userId || "Unknown", value: emp.userId || emp.email || emp.username }));
              if (designationEmployees.length === 0) return null;
              return (
                <div key={designation.id} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>{designation.name}s</label>
                  <CheckboxDropdown label="" options={designationEmployees} selectedValues={dynamicFilters[designation.name] || []} onToggle={(val) => { setDynamicFilters(prev => ({ ...prev, [designation.name]: prev[designation.name].includes(val) ? prev[designation.name].filter(v => v !== val) : [...prev[designation.name], val] })); }} placeholder={`All ${designation.name}s`} showSearch={designationEmployees.length > 5} searchPlaceholder={`Search ${designation.name}...`} />
                </div>
              );
            })}
            {isGroupAdmin && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Companies</label>
                <CheckboxDropdown label="" options={availableCompanies} selectedValues={selectedCompanies} onToggle={(val) => setSelectedCompanies(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])} placeholder="All Companies" />
              </div>
            )}
            {(isGroupAdmin || isCompanyAdmin) && branches.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '12px', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', paddingLeft: '4px' }}>Branches</label>
                <CheckboxDropdown label="" options={branches} selectedValues={filterBranches} onToggle={(val) => setFilterBranches(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])} placeholder="All Branches" />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="print-header d-none d-print-block mb-4" style={{ padding: '0 48px', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', borderBottom: '2px solid #000000', paddingBottom: '10px' }}>
          {logoUrl && <div style={{ flex: '0 0 auto' }}><img src={logoUrl} alt="Logo" className="print-logo" style={{ width: '50px', height: '50px', objectFit: 'contain', borderRadius: '3px' }} /></div>}
          <div style={{ flex: 1, fontFamily: 'Arial, sans-serif', fontSize: '12px', width: '100%' }}>
            {((printSettings || defaultPrintSettings).headerBlocks || []).map((b, i) => (
              <div key={i} style={{ textAlign: b.align, minHeight: '14px' }}>{b.text || ''}</div>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>SALES REPORT</h4>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>{fromDate ? `From: ${getFormattedDate(fromDate)}` : ""} {toDate ? `To: ${getFormattedDate(toDate)}` : ""}</p>
        </div>
      </div>

      <div className="print-wrapper" style={{ flex: 1, overflowY: 'auto', padding: "0 48px 48px" }}>
        
        {/* Dashboard Metrics Section */}
        <div className="dashboard-grid">
          <div className="dashboard-card" style={{ borderTop: '4px solid #3b82f6' }}>
            <span className="dashboard-card-title">Total POS Revenue</span>
            <span className="dashboard-card-value">
              <CurrencySymbol currencyCode={settings.currency || 'AED'} size={18} />
              {aggregates.dashboardMetrics.totalPosRevenue.toFixed(2)}
            </span>
            <span className="dashboard-card-subtitle" style={{ color: '#64748b' }}>
              Net Total: <CurrencySymbol currencyCode={settings.currency || 'AED'} size={10} /> {aggregates.dashboardMetrics.netTotal.toFixed(2)}
            </span>
          </div>
          
          <div className="dashboard-card" style={{ borderTop: '4px solid #22c55e' }}>
            <span className="dashboard-card-title">Cash Payments</span>
            <span className="dashboard-card-value" style={{ color: '#22c55e' }}>
              <CurrencySymbol currencyCode={settings.currency || 'AED'} size={18} />
              {aggregates.dashboardMetrics.cashPayments.toFixed(2)}
            </span>
            <span className="dashboard-card-subtitle" style={{ color: '#22c55e' }}>Physical Cash Sales</span>
          </div>

          <div className="dashboard-card" style={{ borderTop: '4px solid #0ea5e9' }}>
            <span className="dashboard-card-title">Card Payments</span>
            <span className="dashboard-card-value" style={{ color: '#0ea5e9' }}>
              <CurrencySymbol currencyCode={settings.currency || 'AED'} size={18} />
              {aggregates.dashboardMetrics.cardPayments.toFixed(2)}
            </span>
            <span className="dashboard-card-subtitle" style={{ color: '#0ea5e9' }}>Credit & Debit Cards</span>
          </div>

          <div className="dashboard-card" style={{ borderTop: '4px solid #06b6d4' }}>
            <span className="dashboard-card-title">UPI Payments</span>
            <span className="dashboard-card-value" style={{ color: '#06b6d4' }}>
              <CurrencySymbol currencyCode={settings.currency || 'AED'} size={18} />
              {aggregates.dashboardMetrics.upiPayments.toFixed(2)}
            </span>
            <span className="dashboard-card-subtitle" style={{ color: '#06b6d4' }}>Digital Transactions</span>
          </div>

          <div className="dashboard-card" style={{ borderTop: '4px solid #f59e0b' }}>
            <span className="dashboard-card-title">Aggregator Sales</span>
            <span className="dashboard-card-value" style={{ color: '#f59e0b' }}>
              <CurrencySymbol currencyCode={settings.currency || 'AED'} size={18} />
              {aggregates.dashboardMetrics.aggregatorPayments.toFixed(2)}
            </span>
            <span className="dashboard-card-subtitle" style={{ color: '#f59e0b' }}>Zomato, Swiggy, Noon</span>
          </div>

          <div className="dashboard-card" style={{ borderTop: '4px solid #ec4899' }}>
            <span className="dashboard-card-title">Total Discounts Given</span>
            <span className="dashboard-card-value" style={{ color: '#ec4899' }}>
              <CurrencySymbol currencyCode={settings.currency || 'AED'} size={18} />
              {aggregates.dashboardMetrics.totalDiscounts.toFixed(2)}
            </span>
            <span className="dashboard-card-subtitle" style={{ color: '#ec4899' }}>Customer Price Discounts</span>
          </div>

          <div className="dashboard-card" style={{ borderTop: '4px solid #16a34a' }}>
            <span className="dashboard-card-title">Points Redeemed</span>
            <span className="dashboard-card-value" style={{ color: '#16a34a' }}>
              <CurrencySymbol currencyCode={settings.currency || 'AED'} size={18} />
              {aggregates.dashboardMetrics.totalRedeemedPoints.toFixed(2)}
            </span>
            <span className="dashboard-card-subtitle" style={{ color: '#16a34a' }}>Loyalty Discount</span>
          </div>
        </div>

        <div className="premium-card" style={{ maxHeight: '68vh', overflow: 'auto', borderRadius: '10px', boxShadow: '0 5px 25px rgba(0, 0, 0, 0.03)', width: '100%', backgroundColor: '#ffffff', border: 'none' }}>
          <table className="premium-table" style={{ tableLayout: 'fixed', width: '100%', minWidth: '1000px' }}>
            <thead>
              <tr>
                {columnOrder.map((col, index) => {
                  if (col.key === 'companyBranch' && !isGroupAdmin && !isCompanyAdmin) return null;
                  return (
                    <th
                      key={col.key}
                      style={{ textAlign: col.align }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        removeColumn(index);
                      }}
                      className="draggable-header"
                    >
                      {col.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((sale) => (
                <tr key={sale.invoice_no}>
                  {columnOrder.map((col) => {
                    if (col.key === 'companyBranch' && !isGroupAdmin && !isCompanyAdmin) return null;
                    return (
                      <td key={col.key} style={{ textAlign: col.align, wordBreak: 'break-word' }}>
                        {col.key === "invoice_no" && sale.invoice_no}
                        {col.key === "date" && sale.date}
                        {col.key === "time" && sale.time}
                        {col.key === "orderType" && (sale.orderType || "N/A")}
                        {col.key === "companyBranch" && (
                          (!sale.branch_name && !sale.branch) || String(sale.branch_name || sale.branch).toLowerCase() === 'all' || String(sale.branch_name || sale.branch).toLowerCase() === 'all branches'
                            ? (sale.company_name || sale.company || "Company Level")
                            : `${sale.company_name || sale.company || ""} - ${sale.branch_name || sale.branch || ""}`.replace(/^ - | - $/, "")
                        )}
                        {col.key === "total" && formatCurrency(calculateSubtotal(sale), sale)}
                        {col.key === "vat_amount" && formatCurrency(calculateVAT(sale), sale)}
                        {col.key === "redeemed_amount" && formatCurrency(sale.redeemed_amount || 0, sale)}
                        {col.key === "grand_total" && formatCurrency(calculateGrandTotal(sale), sale)}
                        {col.key === "employee" && getEmployeeDisplay(sale)}
                        {col.key === "customer" && (sale.customer || "N/A")}
                        {col.key === "phoneNumber" && (sale.phone || sale.customer_phone || "N/A")}
                        {col.key === "tableNumber" && (sale.tableNumber || "N/A")}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filteredSales.length === 0 && <tr><td colSpan={columnOrder.length} style={{ textAlign: 'center' }}>No results found</td></tr>}
            </tbody>
            {filteredSales.length > 0 && (
              <tfoot className="summary-print-container">
                {Array.from(aggregates.currencyTotals.entries()).map(([curr, totals]) => (
                  <tr key={curr} style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                    {columnOrder.map((col) => {
                      if (col.key === 'companyBranch' && !isGroupAdmin && !isCompanyAdmin) return null;
                      
                      if (col.key === 'total') {
                        return (
                          <td key={col.key} className="text-end" style={{ textAlign: 'right', padding: '20px', color: '#4f46e5', verticalAlign: 'middle' }}>
                            <span style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.5px' }}>Total Amount</span>
                            <span style={{ fontWeight: '700', fontSize: '16px' }}>{getCurrencyFormatter(curr, totals.precision, false, true).format(totals.subtotal)}</span>
                          </td>
                        );
                      }
                      if (col.key === 'vat_amount') {
                        return (
                          <td key={col.key} className="text-end" style={{ textAlign: 'right', padding: '20px', color: '#4f46e5', verticalAlign: 'middle' }}>
                            <span style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.5px' }}>VAT Amount</span>
                            <span style={{ fontWeight: '700', fontSize: '16px' }}>{getCurrencyFormatter(curr, totals.precision, false, true).format(totals.vat)}</span>
                          </td>
                        );
                      }
                      if (col.key === 'redeemed_amount' && totals.redeemedAmount > 0) {
                        return (
                          <td key={col.key} className="text-end" style={{ textAlign: 'right', padding: '20px', color: '#15803d', verticalAlign: 'middle' }}>
                            <span style={{ display: 'block', fontSize: '11px', color: '#15803d', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.5px' }}>Points Discount</span>
                            <span style={{ fontWeight: '700', fontSize: '16px' }}>-{getCurrencyFormatter(curr, totals.precision, false, true).format(totals.redeemedAmount)}</span>
                          </td>
                        );
                      }
                      if (col.key === 'grand_total') {
                        return (
                          <td key={col.key} className="text-end" style={{ textAlign: 'right', padding: '20px', color: '#4f46e5', verticalAlign: 'middle' }}>
                            <span style={{ display: 'block', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.5px' }}>Grand Total</span>
                            <span style={{ fontWeight: '800', fontSize: '18px' }}>{getCurrencyFormatter(curr, totals.precision, false, true).format(totals.grand)}</span>
                          </td>
                        );
                      }
                      
                      // For the first visible column, show the "Total Summary" text
                      const firstVisibleKey = columnOrder.find(c => c.key !== 'companyBranch' || (isGroupAdmin || isCompanyAdmin))?.key;
                      
                      if (col.key === firstVisibleKey) {
                        return (
                          <td key={col.key} style={{ textAlign: 'left', padding: '24px 32px', fontWeight: '800', fontSize: '15px', color: '#0f172a', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                            {curr} Total Summary:
                          </td>
                        );
                      }
                      
                      return <td key={col.key} style={{ padding: '20px', border: 'none' }}></td>;
                    })}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>

        <div className="d-none d-print-block" style={{ marginTop: '20px', borderTop: '2px solid #000000', paddingTop: '10px', width: '100%', fontFamily: 'Arial, sans-serif', fontSize: '12px' }}>
          {((printSettings || defaultPrintSettings).footerBlocks || []).map((b, i) => (
            <div key={i} style={{ textAlign: b.align, minHeight: '14px' }}>{b.text || ''}</div>
          ))}
        </div>
      </div>

      <Modal show={showColumnModal} onHide={() => setShowColumnModal(false)} className="column-modal" centered size="md" contentClassName="border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div style={{ background: 'linear-gradient(145deg, #ffffff, #f9fafb)' }}>
          <Modal.Header closeButton style={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff', padding: '20px 24px' }}>
            <Modal.Title style={{ fontWeight: '800', color: '#111827', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '10px', margin: 0 }}>
              <div style={{ padding: '8px', backgroundColor: '#e0e7ff', borderRadius: '8px', display: 'flex' }}>
                <FaColumns style={{ color: '#4f46e5', fontSize: '1.1rem' }} />
              </div>
              Manage Columns
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '24px', backgroundColor: '#f9fafb' }}>
            
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #f3f4f6', marginBottom: '24px' }}>
              <h6 style={{ fontWeight: '700', color: '#4f46e5', marginBottom: '16px', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center' }}>
                <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#4f46e5', marginRight: '8px' }}></span>
                Add New Column
              </h6>
              
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>Select Field to Add</Form.Label>
                <Form.Select 
                  value={selectedFieldToAdd} 
                  onChange={(e) => setSelectedFieldToAdd(e.target.value)}
                  style={{ border: '2px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '0.95rem', cursor: 'pointer', boxShadow: 'none' }}
                >
                  <option value="">Choose a field...</option>
                  {possibleColumns
                    .filter((p) => !columnOrder.some((c) => c.key === p.key))
                    .map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
              
              <Form.Group className="mb-4">
                <Form.Label style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>Insert Position</Form.Label>
                <Form.Select 
                  value={selectedPosition} 
                  onChange={(e) => setSelectedPosition(Number(e.target.value))}
                  style={{ border: '2px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '0.95rem', cursor: 'pointer', boxShadow: 'none' }}
                >
                  {Array.from({ length: columnOrder.length + 1 }, (_, i) => (
                    <option key={i} value={i}>
                      {i === columnOrder.length ? 'At the end' : `Before "${columnOrder[i]?.label || ''}"`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              <Button 
                onClick={addColumn} 
                disabled={!selectedFieldToAdd} 
                style={{ 
                  width: '100%', 
                  backgroundColor: selectedFieldToAdd ? '#4f46e5' : '#e5e7eb',
                  color: selectedFieldToAdd ? '#ffffff' : '#9ca3af',
                  border: 'none', 
                  padding: '12px', 
                  borderRadius: '8px', 
                  fontWeight: '600', 
                  fontSize: '0.95rem',
                  boxShadow: selectedFieldToAdd ? '0 4px 12px rgba(79, 70, 229, 0.3)' : 'none',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                + Add Column
              </Button>
            </div>
            
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', border: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h6 style={{ fontWeight: '700', color: '#111827', margin: 0, fontSize: '0.95rem' }}>Active Columns</h6>
                <span style={{ fontSize: '0.75rem', color: '#4f46e5', backgroundColor: '#e0e7ff', padding: '4px 10px', borderRadius: '12px', fontWeight: '700' }}>{columnOrder.length} visible</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' }}>
                {columnOrder.map((col, index) => (
                  <div key={col.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#ffffff', transition: 'all 0.2s ease', cursor: 'default' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', backgroundColor: '#f3f4f6', color: '#6b7280', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700' }}>
                        {index + 1}
                      </div>
                      <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>{col.label}</span>
                    </div>
                    <Button 
                      size="sm" 
                      onClick={() => removeColumn(index)}
                      style={{ color: '#ef4444', backgroundColor: '#fee2e2', border: 'none', padding: '4px 12px', borderRadius: '6px', fontWeight: '600', fontSize: '0.8rem', transition: 'all 0.2s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ef4444'; e.currentTarget.style.color = '#ffffff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; }}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '16px', marginBottom: 0, textAlign: 'center', fontStyle: 'italic' }}>Double-click headers in the table to quickly remove them.</p>
            </div>
            
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #e5e7eb', backgroundColor: '#ffffff', padding: '16px 24px' }}>
            <Button 
              onClick={() => setShowColumnModal(false)}
              style={{ backgroundColor: '#f3f4f6', color: '#374151', border: 'none', padding: '10px 24px', borderRadius: '8px', fontWeight: '600', transition: 'all 0.2s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e5e7eb'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
            >
              Done
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={showNoDataModal}
        onHide={() => setShowNoDataModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>Notification</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <h5 className="mb-0">No sales data available.</h5>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNoDataModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SalesReport;
